import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentStep from './StripePaymentStep';
import api from '../api/axios';
import '../pages/new-pages.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

const PLANS = {
  individual: { label: 'Individual', price: '$25', period: '1 year', type: 'membership_single', display: '$25.00 AUD' },
  family:     { label: 'Family',     price: '$50', period: '2 years', type: 'membership_family', display: '$50.00 AUD' },
};

const EMPTY_FORM = {
  first_name: '', last_name: '', dob: '', email: '', phone: '',
  address: '', suburb: '', state: 'VIC', postcode: '',
  spouse_name: '',
  children: [],
};

const gridStyle = { display: 'grid', gap: 16 };
const full = { gridColumn: 'span 2' };

function FormField({ label, k, type = 'text', placeholder = '', style, form, errors, set, setErrors }) {
  return (
    <div className="mncw-form-group" style={style}>
      <label className="mncw-label">{label}</label>
      <input
        type={type}
        className={`mncw-input${errors[k] ? ' error' : ''}`}
        value={form[k]}
        onChange={e => { set(k, e.target.value); setErrors(er => { const { [k]: _, ...r } = er; return r; }); }}
        placeholder={placeholder}
      />
      {errors[k] && <span className="mncw-error-text">{errors[k]}</span>}
    </div>
  );
}

export default function MembershipForm() {
  const [plan, setPlan] = useState('family');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [clientSecret, setClientSecret] = useState('');
  const [amount, setAmount] = useState(0);
  const [pendingId, setPendingId] = useState('');
  const [saving, setSaving] = useState(false);

  const selected = PLANS[plan];

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addChild = () => set('children', [...form.children, '']);
  const removeChild = i => set('children', form.children.filter((_, idx) => idx !== i));
  const setChild = (i, v) => set('children', form.children.map((c, idx) => idx === i ? v : c));

  function validate() {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim()) e.last_name = 'Required';
    if (!form.dob) e.dob = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.phone.trim()) e.phone = 'Required';
    if (!form.address.trim()) e.address = 'Required';
    if (!form.suburb.trim()) e.suburb = 'Required';
    if (!form.postcode.trim()) e.postcode = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleProceedToPayment(e) {
    e.preventDefault();
    if (!validate()) return;
    try {
      const { data: rec } = await api.post('/members', { ...form, membership_type: plan, payment_intent_id: '' });
      setPendingId(rec.id);
      const { data } = await api.post('/payments/intent', { type: selected.type, quantity: 1, recordId: rec.id });
      setClientSecret(data.clientSecret);
      setAmount(data.amount);
      setStep(2);
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || err.message });
    }
  }

  async function handlePaymentSuccess(paymentIntentId) {
    setSaving(true);
    try {
      await api.post('/members', { ...form, id: pendingId, membership_type: plan, payment_intent_id: paymentIntentId });
      setStep(3);
    } catch {
      setStep(3);
    } finally {
      setSaving(false);
    }
  }

  function handlePlanChange(newPlan) {
    setPlan(newPlan);
    setForm(EMPTY_FORM);
    setErrors({});
    setStep(1);
    setClientSecret('');
    setPendingId('');
  }

  // ── Step 3: Success ──────────────────────────────────────────────────────────
  if (step === 3) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <i className="fas fa-check-circle" style={{ fontSize: '4rem', color: 'green', display: 'block', marginBottom: 16 }}></i>
        <h3 style={{ color: 'var(--dark-blue)', marginBottom: 8 }}>Welcome to MNCW!</h3>
        <p style={{ color: 'var(--text-light)', marginBottom: 4 }}>
          Thank you, <strong>{form.first_name}</strong>! Your <strong>{selected.label} Membership</strong> ({selected.period}) is confirmed.
        </p>
        <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: 24 }}>
          A confirmation will be sent to <strong>{form.email}</strong>.
        </p>
        <button
          className="btn-mncw btn-mncw-outline"
          onClick={() => { setForm(EMPTY_FORM); setStep(1); setPendingId(''); setClientSecret(''); }}
        >
          Register Another Member
        </button>
      </div>
    );
  }

  // ── Step 2: Payment ──────────────────────────────────────────────────────────
  if (step === 2 && clientSecret) {
    return (
      <div>
        <p style={{ color: 'var(--text-light)', marginBottom: 24 }}>
          <strong style={{ color: 'var(--dark-blue)' }}>{form.first_name} {form.last_name}</strong> · {selected.label} Membership ({selected.period}) · {selected.display}
        </p>
        <Elements stripe={stripePromise} options={{
          clientSecret,
          appearance: {
            theme: 'stripe',
            rules: {
              '.p-LinkOptIn': { display: 'none' },
              '.p-LinkDefaultOptIn': { display: 'none' },
            },
          },
        }}>
          <StripePaymentStep
            amount={amount}
            onSuccess={handlePaymentSuccess}
            onBack={() => setStep(1)}
            loading={saving}
          />
        </Elements>
      </div>
    );
  }

  // ── Step 1: Details ──────────────────────────────────────────────────────────
  return (
    <form onSubmit={handleProceedToPayment} noValidate>

      {/* Plan selector */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        {Object.entries(PLANS).map(([key, p]) => (
          <button
            key={key}
            type="button"
            onClick={() => handlePlanChange(key)}
            style={{
              flex: 1,
              padding: '14px 16px',
              borderRadius: 8,
              border: `2px solid ${plan === key ? 'var(--primary-blue)' : 'var(--light-bg)'}`,
              background: plan === key ? 'var(--light-blue)' : '#fff',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '1.2rem', color: 'var(--dark-blue)' }}>{p.price}</div>
            <div style={{ fontWeight: 600, color: 'var(--dark-blue)', fontSize: '0.9rem' }}>{p.label}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{p.period}</div>
          </button>
        ))}
      </div>

      {/* Plan summary badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: 'var(--light-blue)', borderRadius: 8, padding: '16px 20px', marginBottom: 24, border: '1px solid rgba(0,56,147,0.15)' }}>
        <div style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--dark-blue)' }}>{selected.price}</div>
        <div>
          <div style={{ fontWeight: 600, color: 'var(--dark-blue)' }}>{selected.label} Membership</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
            {plan === 'individual' ? 'Single person · Valid for 1 year' : 'Couple & children · Valid for 2 years'}
          </div>
        </div>
        <span style={{ marginLeft: 'auto', background: 'var(--primary-blue)', color: '#fff', borderRadius: 4, padding: '2px 10px', fontSize: '0.8rem', fontWeight: 600 }}>
          {selected.period}
        </span>
      </div>

      {/* Personal details */}
      <div style={{ ...gridStyle, gridTemplateColumns: '1fr 1fr' }}>
        <FormField label="First Name *" k="first_name" placeholder="First name" form={form} errors={errors} set={set} setErrors={setErrors} />
        <FormField label="Last Name *" k="last_name" placeholder="Last name" form={form} errors={errors} set={set} setErrors={setErrors} />
        <FormField label="Date of Birth *" k="dob" type="date" form={form} errors={errors} set={set} setErrors={setErrors} />
        <FormField label="Phone Number *" k="phone" type="tel" placeholder="04xx xxx xxx" form={form} errors={errors} set={set} setErrors={setErrors} />
        <FormField label="Email Address *" k="email" type="email" placeholder="your@email.com" style={full} form={form} errors={errors} set={set} setErrors={setErrors} />
        <FormField label="Street Address *" k="address" placeholder="123 Example St" style={full} form={form} errors={errors} set={set} setErrors={setErrors} />
        <FormField label="Suburb *" k="suburb" placeholder="Melton" form={form} errors={errors} set={set} setErrors={setErrors} />
        <div className="mncw-form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label className="mncw-label">State</label>
            <select className="mncw-input" value={form.state} onChange={e => set('state', e.target.value)} style={{ cursor: 'pointer' }}>
              {AU_STATES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="mncw-label">Postcode *</label>
            <input className={`mncw-input${errors.postcode ? ' error' : ''}`} value={form.postcode}
              onChange={e => { set('postcode', e.target.value); setErrors(er => { const { postcode: _, ...r } = er; return r; }); }}
              placeholder="3337" />
            {errors.postcode && <span className="mncw-error-text">{errors.postcode}</span>}
          </div>
        </div>
      </div>

      {/* Family extras — only for family plan */}
      {plan === 'family' && (
        <div style={{ marginTop: 8 }}>
          <div className="mncw-form-group">
            <label className="mncw-label">Spouse / Partner Name</label>
            <input className="mncw-input" value={form.spouse_name} onChange={e => set('spouse_name', e.target.value)} placeholder="Full name (optional)" />
          </div>

          <div className="mncw-form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label className="mncw-label" style={{ marginBottom: 0 }}>Children (optional)</label>
              <button type="button" onClick={addChild} style={{ background: 'none', border: '1.5px solid var(--primary-blue)', borderRadius: 20, padding: '4px 14px', color: 'var(--primary-blue)', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                <i className="fas fa-plus" style={{ marginRight: 4 }}></i>Add Child
              </button>
            </div>
            {form.children.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="mncw-input" value={c} onChange={e => setChild(i, e.target.value)} placeholder={`Child ${i + 1} name`} />
                <button type="button" onClick={() => removeChild(i)} style={{ background: 'none', border: '1.5px solid var(--primary-red)', borderRadius: 8, padding: '0 12px', color: 'var(--primary-red)', cursor: 'pointer', flexShrink: 0 }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {errors.submit && (
        <div style={{ background: '#fff3f3', border: '1px solid #ffcccc', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: 'var(--primary-red)', fontSize: '0.9rem' }}>
          <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }}></i>{errors.submit}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, paddingTop: 20, borderTop: '2px solid var(--light-bg)' }}>
        <span style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>Total: <strong style={{ color: 'var(--dark-blue)', fontSize: '1.2rem' }}>{selected.display}</strong></span>
        <button type="submit" className="btn-mncw">
          Proceed to Payment <i className="fas fa-arrow-right" style={{ marginLeft: 8 }}></i>
        </button>
      </div>
    </form>
  );
}
